import React, {ChangeEvent, CSSProperties, ReactElement, SyntheticEvent} from 'react';
import './Confirm.less';
import '../stylesheets/form.less';
import Select, {SelectHandle} from "./Select/Select";
import Help from "./Help/Help";
import CheckboxWithState, {CheckboxWithStateHandle} from "./CheckboxWithState";
import {Provider} from "react-redux";
import {QueryClientProvider} from '@tanstack/react-query';
import store from "../store/store";
import {queryClient} from "../lib/queryClient";
import util from "../lib/util";
import Confirm from './dialogs/Confirm';
import i18n from "../i18n/i18n";
import {isMobile} from "../lib/picos/browserFns";
import type {VoidCallback, VoidCallbackPromise} from "../@types/functions";
import {InputAdornment, TextField} from "@mui/material";
import showImmediate from "./dialogs/showImmediate";
import type {SelectOption} from "../@types/select";

export type Closeable<T> = ReactElement & {
    props: {
        onClose?: (result: T) => void;
    }
};

export type SelectDialogOptions = {
    isMulti?: boolean,
    allowNone?: boolean,
    onOk?: (value: any, option: any) => void,
    onCancel?: VoidCallback,
    buttonCaptionOk?: string,
    buttonCaptionCancel?: string,
    width?: string | number
}

export type DialogOptions = {
    title?: string,
    message: string | ReactElement,
    okLabel?: string,
    cancelLabel?: string,
    onOk: VoidCallback,
    onCancel?: VoidCallback,
    useNative?: boolean,
}

/**
 * Maximum default width for a dialog box.
 */
const MAX_WIDTH = '40em';

function confirmError(title: string, message: string, buttonCaption?: string, onOk?: VoidCallback) {
    if (!buttonCaption) buttonCaption = 'OK';

    showImmediate(onClose => (
        <Confirm title={title}
                 message={message}
                 buttonCaptionOk={buttonCaption}
                 onOk={onOk ? () => {
                     onOk();
                     onClose(null);
                 } : null}
                 severity='error'
                 hideCancel={true}
                 styles={{maxWidth: MAX_WIDTH}}
        />
    ));
}

function confirmWarning(title: string, message: string | ReactElement, buttonCaption?: string, onOk?: VoidCallback) {
    if (!buttonCaption) buttonCaption = 'OK';

    showImmediate(onClose => (
        <Confirm title={title}
                 message={message}
                 buttonCaptionOk={buttonCaption}
                 onOk={() => {
                     // Callers such as EditableTable's "cannot delete" notice pass no handler.
                     onOk?.();
                     onClose();
                 }}
                 severity='warning'
                 hideCancel={true}
                 styles={{maxWidth: MAX_WIDTH}}
        />
    ));
}

function confirmSuccess(title: string, message: string, buttonCaption?: string) {
    if (!buttonCaption) buttonCaption = 'OK';

    showImmediate(onClose => (
        <Confirm title={title}
                 message={message}
                 buttonCaptionOk={buttonCaption}
                 onOk={onClose}
                 severity='success'
                 hideCancel={true}
                 styles={{maxWidth: MAX_WIDTH}}
        />
    ));
}

/**
 * Better way to call confirmWarningOkCancel.
 *
 * @param {DialogOptions} options
 */
function confirmWarningOkCancelWithOptions(options: DialogOptions) {
    // Set the defaults
    if (!options) throw new Error('No options sent to confirmWarningOkCancel');

    let {title, message, okLabel, cancelLabel, onOk, onCancel, useNative} = options;

    if (!message) title = '';
    if (!okLabel) okLabel = 'OK';
    if (!cancelLabel) cancelLabel = 'Cancel';
    if (!onCancel) onCancel = util.noop;

    if (useNative) {
        // eslint-disable-next-line no-alert -- useNative path intentionally uses the browser-native confirm dialog
        if (window.confirm(message as string)) onOk();
        else onCancel();
    }
    else {
        confirmWarningOkCancel(title, message, okLabel, cancelLabel, onOk, onCancel);
    }
}

const confirmChangesMade = (doWork: VoidCallback) => {
    return confirmWarningOkCancelWithOptions({
        title: i18n.c('changesMade') as string,
        message: i18n.c('changesMadeDescription') as string,
        okLabel: 'Lose Changes',
        cancelLabel: 'Stay Here',
        onOk: doWork,
        useNative: false,
    });
}

const confirmWarningOkCancelNative = (
    _title: string = 'Warning',
    message: string,
    _buttonCaptionOK: string = 'OK',
    _buttonCaptionCancel: string = 'Cancel',
    onOK: VoidCallback = util.noop,
    onCancel: VoidCallback = util.noop
) => {
    // eslint-disable-next-line no-alert -- this helper is the native-confirm implementation by design
    if (window.confirm(message)) onOK();
    else onCancel();
};

/**
 * Puts up a warning box with a title, message, and OK/Cancel buttons.
 *
 * @param {string} title - the title of the messagebox
 * @param {string|JSX.Element} message - the message text
 * @param {string} buttonCaptionOK - custom caption for the OK button, defaults to OK
 * @param {string} buttonCaptionCancel - custom caption for the Cancel button, defaults to Cancel
 * @param {function} onOk - function to execute on OK button press, defaults to noop
 * @param {function} onCancel - function to execute on Cancel button press, defaults to noop
 */
function confirmWarningOkCancel(
    title: string = 'Warning',
    message: string | ReactElement,
    buttonCaptionOK: string = 'OK',
    buttonCaptionCancel: string = 'Cancel',
    onOk: VoidCallback | VoidCallbackPromise = util.noop,
    onCancel: VoidCallback | VoidCallbackPromise = util.noop,
    okColor?: 'success' | 'error' | 'primary' | 'warning' | 'info'
) {
    showImmediate<boolean>(onClose => (
        <Confirm title={title}
                 message={message}
                 buttonCaptionOk={buttonCaptionOK}
                 buttonCaptionCancel={buttonCaptionCancel}
                 okColor={okColor}
                 onOk={() => {
                     const result = onOk();

                     if (result instanceof Promise) {
                         result.then(() => onClose(true));
                     }
                     else onClose(true);
                 }}
                 onCancel={() => {
                     const result = onCancel();

                     if (result instanceof Promise) {
                         result.then(() => onClose(true));
                     }
                     else onClose(true);
                 }}
            // noButtons={true}
        />
    ));
}

/**
 * Put up a warning, with a list of options to choose.
 * The selected option will be sent to the onOk handler.

 */
function confirmWarningOkCancelWithSelection<V = string, D = any>(
    title: string | ReactElement = 'Warning',
    message: string | ReactElement,
    options: Array<SelectOption<V, D>>,
    buttonCaptionOK: string = 'OK',
    buttonCaptionCancel: string = 'Cancel',
    onOK: (value: V, data?: D) => void = util.noop,
    onCancel?: VoidCallback,
    isMulti?: boolean,
    styles?: CSSProperties
) {
    if (!options) throw new Error('Options are required for this function');

    showImmediate<V>((onClose) => {
        const selectRef = React.createRef<SelectHandle<V, D>>();

        if (!styles) styles = {};
        styles.overflowY = 'visible';
        styles.marginTop = '1em';

        // Without usesExtraFields, Select passes just the value (V) to onChange
        let selectedValue: V = options[0]?.value;

        const onChange = (value: V) => {
            selectedValue = value;
        }

        return <Provider store={store}>
            <Confirm title={title}
                     message={message}
                     buttonCaptionOk={buttonCaptionOK}
                     buttonCaptionCancel={buttonCaptionCancel}
                     onOk={() => {
                         onOK(selectedValue);
                         onClose(selectedValue);
                     }}
                     onCancel={() => {
                         onCancel?.();
                         onClose(undefined);
                     }}
                     styles={{width: 'fit-content', ...styles}}
            >
                <div className="Confirm-Select" style={{minWidth: '20em', ...styles}}>
                    <Select name='confirm-options'
                            ref={selectRef}
                            options={options}
                            isMulti={isMulti}
                            allowNone={false}
                            controlled={true}
                            onChange={onChange}
                            autoFocus={!isMobile({tablet: true, featureDetect: true})}
                    />
                </div>
            </Confirm>
        </Provider>
    });
}

/**
 * TODO: This seems to assume the input is always of type money...
 * Put up a warning, with a list of options to choose.
 * The selected option will be sent to the onOk handler.
 *
 * @param {string} title
 * @param {string|Element} message
 * @param {string} type - text, number
 * @param {string} buttonCaptionOK
 * @param {string} buttonCaptionCancel
 * @param {function} onOK
 * @param {function} [onCancel]
 * @param {number} [defaultValue]
 * @param {string} [label]
 */
function confirmSuccessOkCancelWithInput(
    title: string = 'Success',
    message: string | ReactElement,
    type: 'text' | 'number' | 'money' | 'percent',
    buttonCaptionOK: string = 'OK',
    buttonCaptionCancel: string = 'Cancel',
    onOK: (amount: number) => void,
    onCancel?: VoidCallback,
    defaultValue?: number,
    label?: string
) {
    if (!label) label = 'Amount';

    const inputType = (type === 'money' || type === 'percent') ? 'number' : type;

    showImmediate<number>(onClose => {
        let result = {amount: defaultValue || 0};

        const handleResult = (e: React.FormEvent<HTMLDivElement>) => {
            const target = e.target as HTMLInputElement;
            result.amount = target.value ? Number(target.value) : 0;
        }

        const inputRef = React.createRef<any>();

        const onFocus = () => {
            inputRef.current.select();
        }

        const onLoad = () => {
            inputRef.current.focus();
        }

        return <Confirm title={title}
                        message={message}
                        buttonCaptionOk={buttonCaptionOK}
                        buttonCaptionCancel={buttonCaptionCancel}
                        severity='success'
                        onOk={(e?: SyntheticEvent) => {
                            try {
                                e?.preventDefault();
                                e?.stopPropagation();

                                onOK(result.amount);
                            }
                            catch (err) {
                                console.warn(err);
                            }

                            onClose(result.amount);
                        }}
                        onCancel={(e?: SyntheticEvent) => {
                            try {
                                e?.preventDefault();
                                e?.stopPropagation();

                                (onCancel ?? util.noop)();
                            }
                            catch (err) {
                                console.warn(err);
                            }

                            onClose(null);
                        }}
                        styles={{maxWidth: MAX_WIDTH}}
        >
            <TextField ref={inputRef}
                       label={label}
                       className='money'
                       required={true}
                       autoFocus={!isMobile({tablet: true, featureDetect: true})}
                       type={inputType}
                       value={defaultValue}
                       onFocus={onFocus}
                       onLoad={onLoad}
                       onInput={handleResult}
                       slotProps={{
                           input: {
                               startAdornment: (type === 'money'
                                                ? <InputAdornment position="start" tabIndex={-1}>$</InputAdornment>
                                                : null),
                               endAdornment: (type === 'percent'
                                              ? <InputAdornment position="end" tabIndex={-1}>%</InputAdornment>
                                              : null),
                           },
                       }}
            />
        </Confirm>
    });
}

/**
 * Put up a warning, with a checkbox available.
 * The selected option will be sent to the onOk handler.
 *
 * @param {string} title
 * @param {string|Element} message
 * @param {string} label - Checkbox label
 * @param {string} hint - optional hint
 * @param {string} buttonCaptionOK
 * @param {boolean} checked - default is not checked
 * @param {string} buttonCaptionCancel
 * @param {function} onOK
 * @param {function} onCancel
 */
const confirmWarningOkCancelWithCheckbox = (
    title: string = 'Warning',
    message: string | ReactElement,
    label: string,
    hint: string,
    checked: boolean,
    buttonCaptionOK: string = 'OK',
    buttonCaptionCancel: string = 'Cancel',
    onOK: Function = util.noop,
    onCancel: Function = util.noop
) => {
    if (!label) throw new Error('Label is required for this function');

    showImmediate<boolean>(onClose => {
        const checkboxRef = React.createRef<CheckboxWithStateHandle>();

        return <Confirm title={title}
                        message={message}
                        buttonCaptionOk={buttonCaptionOK}
                        buttonCaptionCancel={buttonCaptionCancel}
                        severity='warning'
                        onOk={(e?: SyntheticEvent) => {
                            try {
                                e?.preventDefault();
                                e?.stopPropagation();

                                onOK(checkboxRef.current.getChecked());
                            }
                            catch (err) {
                                console.warn(err);
                            }

                            onClose(checkboxRef.current.getChecked());
                        }}
                        onCancel={(e?: SyntheticEvent) => {
                            try {
                                e?.preventDefault();
                                e?.stopPropagation();

                                (onCancel ?? util.noop)();
                            }
                            catch (err) {
                                console.warn(err);
                            }

                            onClose(null);
                        }}
                        styles={{maxWidth: MAX_WIDTH}}
        >
            <div style={{display: 'flex', flexDirection: 'row', maxWidth: 'fit-content', marginTop: '1em'}}>
                <CheckboxWithState ref={checkboxRef}
                                   checked={checked}
                                   label={label}
                                   onClick={(e: MouseEvent) => e.preventDefault()}
                />
                {hint && <Help message={hint} title='hint' fixedPosition={true}/>}
            </div>
        </Confirm>;
    });
}

/**
 * Puts up a success box with a title, message, and OK/Cancel buttons.
 *
 * @param {string} title - the title of the messagebox
 * @param {string|JSX.Element} message - the message text
 * @param {string} buttonCaptionOK - custom caption for the OK button, defaults to OK
 * @param {string} buttonCaptionCancel - custom caption for the Cancel button, defaults to Cancel
 * @param {function} onOk - function to execute on OK button press, defaults to noop
 * @param {function} onCancel - function to execute on Cancel button press, defaults to noop
 */
const confirmSuccessOkCancel = (
    title: string = 'Success',
    message: string | ReactElement,
    buttonCaptionOK: string = 'OK',
    buttonCaptionCancel: string = 'Cancel',
    onOk: VoidCallback,
    onCancel?: VoidCallback
) => {
    showImmediate<boolean>(onClose => (
        <Confirm title={title}
                 message={message}
                 severity='success'
                 buttonCaptionOk={buttonCaptionOK}
                 buttonCaptionCancel={buttonCaptionCancel}
                 onOk={() => {
                     onOk();
                     onClose(true);
                 }}
                 onCancel={() => {
                     onCancel?.();
                     onClose(false);
                 }}
                 styles={{maxWidth: MAX_WIDTH}}
        />
    ));
}

/**
 * Show a modal dialog with a custom component
 *
 * @param {string} title - title for the dialog
 * @param {JSX.Element} component - a React component
 * @param {object} styles - styles to override the existing styles
 * @param {string} [okText]
 * @param {string} [cancelText]
 * @param {function} [onOk]
 * @param {function} [onCancel]
 */
function dialog<T>(
    title: string,
    component: Closeable<T>,
    styles: object,
    okText?: string,
    cancelText?: string,
    onOk?: VoidCallback,
    onCancel?: VoidCallback
) {
    showImmediate<T>(onClose => {
        // Added this to handle any weird errors during close
        const _onClose = (result: T) => {
            try {
                onClose(result);
            }
            catch (err) {
            }
        };

        return <Provider store={store}>
            <Confirm title={title}
                     onOk={onOk}
                     onCancel={onCancel}
                     styles={styles}
                     buttonCaptionOk={okText}
                     buttonCaptionCancel={cancelText}
            >
                {React.cloneElement(component, {onClose: _onClose})}
            </Confirm>
        </Provider>;
    });
}

/**
 * Show a modal dialog with a custom component, but no buttons
 * @param {string} title
 * @param {JSXElement} component
 * @param {CSSProperties} [styles]
 * @param {function} [onDismiss] - called when the user closes the dialog with X or Escape. A dialog
 *   with onDismiss treats closing as a decision, so a click outside it (which may be meant for
 *   another dialog) leaves it open.
 */
function dialogNoButtons<T>(title: string, component: Closeable<T>, styles?: CSSProperties, onDismiss?: VoidCallback) {
    showImmediate<T>(onClose => {
        // Added this to handle any weird errors during close
        const _onClose = (result: T) => {
            try {
                onClose(result);
            }
            catch (err) {
                console.warn('Error calling onClose()', err);
            }
        };

        return <Provider store={store}>
            <QueryClientProvider client={queryClient}>
                {/* A dismissed dialog closes its root too: the host div it leaves behind would count as an open dialog to the reload gate */}
                <Confirm title={title} styles={styles} noButtons={true} closeOnClickAway={!onDismiss}
                         onCancel={onDismiss && (() => { onDismiss(); _onClose(undefined as T); })}>
                    {React.cloneElement(component, {onClose: _onClose})}
                </Confirm>
            </QueryClientProvider>
        </Provider>;
    });
}

/**
 * A typed dialog that also has access to the app's router.
 *
 * @param {string} title
 * @param {ReactElement} component
 * @param {CSSProperties} styles
 * @param {VoidCallback} [onCancel] - called when the dialog is closed by Escape, its X, or a
 *        click outside it (the component's own buttons call its onClose instead)
 */
function dialogNoButtonsRouter<T>(title: string, component: Closeable<T>, styles: CSSProperties, onCancel?: VoidCallback) {
    showImmediate<T>(onClose => {
        // Added this to handle any weird errors during close
        const _onClose = (result: T) => {
            try {
                onClose(result);
            }
            catch (err) {
                console.warn('Error calling onClose()', err);
            }
        };

        return <Provider store={store}>
            <QueryClientProvider client={queryClient}>
                <Confirm title={title} styles={styles} noButtons={true} onCancel={onCancel}>
                    {React.cloneElement(component, {onClose: _onClose})}
                </Confirm>
            </QueryClientProvider>
        </Provider>;
    });
}

function dialogWithCancel<T>(title: string, component: Closeable<T>, styles?: CSSProperties) {
    showImmediate(onClose => {
        // Added this to handle any weird errors during close
        const _onClose = () => {
            try {
                onClose(null);
            }
            catch (err) {
            }
        };

        return <Provider store={store}>
            <Confirm onCancel={_onClose} title={title} severity='info' styles={styles} onOk={null}>
                {React.cloneElement(component, {onClose: _onClose})}
            </Confirm>
        </Provider>;
    });
}

export {
    confirmSuccess,
    confirmWarning,
    confirmError,
    confirmWarningOkCancel,
    confirmWarningOkCancelNative,
    confirmWarningOkCancelWithOptions,
    confirmSuccessOkCancel,
    confirmWarningOkCancelWithSelection,
    confirmWarningOkCancelWithCheckbox,
    confirmSuccessOkCancelWithInput,
    confirmChangesMade,
    dialog,
    dialogNoButtons,
    dialogNoButtonsRouter,
    dialogWithCancel
};
